import { queryKeys } from './queryKeys.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function createUserDataQueries({ supabase }) {
  // src/hooks/queries/useUserData.js

  /**
   * Fetch user access data (role, organization, status)
   * 
   * @param {string} userId - User ID
   * @returns {Object} Query result with user access data
   */
  function useUserAccess(userId) {
    return useQuery({
      queryKey: queryKeys.userAccess(userId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('user_access')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (error) throw error;
        return data;
      },
      enabled: !!userId,
      staleTime: 10 * 60 * 1000, // 10 minutes - user data doesn't change often
    });
  }

  /**
   * Fetch user progress for a specific course
   * 
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @returns {Object} Query result with progress data
   */
  function useUserProgress(userId, courseId) {
    return useQuery({
      queryKey: queryKeys.userProgress(userId, courseId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .order('last_accessed', { ascending: false });
        
        if (error) throw error;
        return data || [];
      },
      enabled: !!userId && !!courseId,
    });
  }

  /**
   * Fetch all user progress across all courses
   * 
   * @param {string} userId - User ID
   * @returns {Object} Query result with all progress data
   */
  function useUserAllProgress(userId) {
    return useQuery({
      queryKey: queryKeys.userAllProgress(userId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('user_progress')
          .select('*, dynamic_courses(title)')
          .eq('user_id', userId)
          .order('last_accessed', { ascending: false });
        
        if (error) throw error;
        return data || [];
      },
      enabled: !!userId,
    });
  }

  /**
   * Update user profile/access information
   * 
   * @returns {Object} Mutation object
   */
  function useUpdateUserProfile() {
    const queryClient = useQueryClient();
    
    return useMutation({
      mutationFn: async ({ userId, updates }) => {
        const { data, error } = await supabase
          .from('user_access')
          .update(updates)
          .eq('id', userId)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      },
      onSuccess: (data, variables) => {
        queryClient.setQueryData(['user_access', variables.userId], data);
      },
    });
  }

  /**
   * Track user progress for a page/module
   * 
   * @returns {Object} Mutation object
   */
  function useTrackProgress() {
    const queryClient = useQueryClient();
    
    return useMutation({
      mutationFn: async (progressData) => {
        const { data, error } = await supabase
          .from('user_progress')
          .upsert([progressData], {
            onConflict: 'user_id,course_id,submodule_id',
          })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      },
      onSuccess: (data, variables) => {
        // Invalidate progress queries
        queryClient.invalidateQueries(['user_progress', variables.user_id]);
        if (variables.course_id) {
          queryClient.invalidateQueries(['user_progress', variables.user_id, variables.course_id]);
        }
      },
    });
  }

  /**
   * Fetch user's quiz responses
   * 
   * @param {string} userId - User ID
   * @param {string} courseId - Optional course ID filter
   * @returns {Object} Query result with quiz responses
   */
  function useQuizResponses(userId, courseId = null) {
    return useQuery({
      queryKey: queryKeys.quizResponses(userId, courseId),
      queryFn: async () => {
        let query = supabase
          .from('quiz_responses')
          .select('*')
          .eq('user_id', userId);
        
        if (courseId) {
          query = query.eq('course_id', courseId);
        }
        
        query = query.order('submitted_at', { ascending: false });

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
      },
      enabled: !!userId,
    });
  }

  /**
   * Submit quiz response
   * 
   * @returns {Object} Mutation object
   */
  function useSubmitQuizResponse() {
    const queryClient = useQueryClient();
    
    return useMutation({
      mutationFn: async (quizData) => {
        const { data, error } = await supabase
          .from('quiz_responses')
          .upsert([quizData], {
            onConflict: 'user_id,page_id,attempt_number',
            ignoreDuplicates: true,
          })
          .select();
        
        if (error) throw error;
        return data?.[0] || null;
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['quiz_responses', variables.user_id]);
        if (variables.course_id) {
          queryClient.invalidateQueries(['quiz_responses', variables.user_id, variables.course_id]);
        }
      },
    });
  }

  /**
   * Fetch organization data
   * 
   * @param {string} orgName - Organization name
   * @returns {Object} Query result with organization data
   */
  function useOrganization(orgName) {
    return useQuery({
      queryKey: queryKeys.organization(orgName),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .eq('name', orgName)
          .single();
        
        if (error) throw error;
        return data;
      },
      enabled: !!orgName,
      staleTime: 15 * 60 * 1000, // 15 minutes
    });
  }

  /**
   * Fetch organization by subdomain
   * 
   * @param {string} subdomain - Organization subdomain
   * @returns {Object} Query result with organization data
   */
  function useOrganizationBySubdomain(subdomain) {
    return useQuery({
      queryKey: queryKeys.organizationBySubdomain(subdomain),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .eq('subdomain', subdomain)
          .maybeSingle();
        
        if (error) throw error;
        return data;
      },
      enabled: !!subdomain,
      staleTime: 15 * 60 * 1000, // 15 minutes
    });
  }


  return { useUserAccess, useUserProgress, useUserAllProgress, useUpdateUserProfile, useTrackProgress, useQuizResponses, useSubmitQuizResponse, useOrganization, useOrganizationBySubdomain };
}
